import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

// API URL from environment variable or default
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Create the context
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [otpSent, setOtpSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');

  // Add token to all axios requests and configure axios
  useEffect(() => {
    // Set token in axios instance headers
    if (token) {
      axiosInstance.defaults.headers.common[
        'Authorization'
      ] = `Bearer ${token}`;
    } else {
      delete axiosInstance.defaults.headers.common['Authorization'];
    }

    // Add response interceptor to handle token expiration
    const interceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if error is due to authentication (401 status)
        if (error.response && error.response.status === 401) {
          // Check if the error is specifically about token expiration
          const errorDetail = error.response.data?.detail || '';
          const isExpiredToken =
            errorDetail.includes('expired') ||
            errorDetail.includes('invalid') ||
            errorDetail.includes('Authentication failed');

          if (isExpiredToken) {
            logout();
          }
        }
        return Promise.reject(error);
      }
    );

    // Clean up interceptor on unmount
    return () => {
      axiosInstance.interceptors.response.eject(interceptor);
    };
  }, [token]);

  // Fetch user on initial load
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const res = await axiosInstance.get('/users/me', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setCurrentUser(res.data);
      } catch (err) {
        // If token is invalid, clear it
        if (err.response?.status === 401) {
          logout();
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [token]);

  const sendPhoneOTP = async (phone) => {
    setLoading(true);
    setError('');
    setPhoneNumber(phone);

    try {
      await axiosInstance.post('/auth/phone/send-otp', {
        phone,
      });

      setOtpSent(true);
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to send OTP';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Check if a user exists with the given phone number
  const checkUserExists = async (phone) => {
    try {
      const res = await axiosInstance.post('/auth/check-user', { phone });
      return res.data.exists;
    } catch (err) {
      // If the endpoint doesn't exist or there's an error, we'll assume the user doesn't exist
      // This is a fallback in case the backend doesn't have this endpoint yet
      return false;
    }
  };

  const verifyPhoneOTP = async (otp, userData = {}) => {
    setLoading(true);
    setError('');

    try {
      const verifyData = {
        phone: phoneNumber,
        otp,
        name: userData.name || '',
        email: userData.email || null,
      };

      // Add address if provided
      if (userData.address) {
        // Ensure all address fields are strings to prevent object rendering issues
        const sanitizedAddress = {};
        Object.keys(userData.address).forEach((key) => {
          sanitizedAddress[key] =
            userData.address[key] !== null &&
            userData.address[key] !== undefined
              ? String(userData.address[key])
              : '';
        });
        verifyData.address = sanitizedAddress;
      }

      const res = await axiosInstance.post('/auth/phone/verify', verifyData);
      const { access_token, user } = res.data;

      // Save token and set current user
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setCurrentUser(user);
      setOtpSent(false);

      return user;
    } catch (err) {
      // Handle validation errors from the backend
      let errorMessage = 'OTP verification failed';

      if (err.response?.data) {
        // If it's a structured error object, convert it to a string message
        if (
          typeof err.response.data === 'object' &&
          err.response.data !== null
        ) {
          if (err.response.data.detail) {
            errorMessage = err.response.data.detail;
          } else {
            // Convert the error object to a readable message
            errorMessage = Object.entries(err.response.data)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ');
          }
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Remove token from localStorage and state
    localStorage.removeItem('token');
    setToken('');
    setCurrentUser(null);

    // Call backend logout endpoint (best effort)
    axiosInstance.post('/auth/logout').catch(() => {
      // Ignore logout errors as user is already being logged out
    });
  };

  const updateProfile = async (userData) => {
    setLoading(true);
    setError('');

    try {
      // Ensure token is included for this request
      const currentToken = localStorage.getItem('token');

      if (!currentToken) {
        setError('Authentication required');
        throw new Error('Authentication required');
      }

      const res = await axiosInstance.put('/users/me', userData, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setCurrentUser(res.data);
      return res.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || 'Failed to update profile';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Get user addresses
  const getUserAddresses = async () => {
    try {
      // Ensure token is included for this request
      const currentToken = localStorage.getItem('token');

      if (!currentToken) {
        setError('Authentication required');
        throw new Error('Authentication required');
      }

      const res = await axiosInstance.get('/users/me/addresses', {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      return res.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || 'Failed to fetch addresses';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Add a new address
  const addAddress = async (addressData) => {
    try {
      // Ensure token is included for this request
      const currentToken = localStorage.getItem('token');

      if (!currentToken) {
        setError('Authentication required');
        throw new Error('Authentication required');
      }

      const res = await axiosInstance.post('/users/me/addresses', addressData, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      return res.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || 'Failed to add address';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Update an existing address
  const updateAddress = async (addressId, addressData) => {
    try {
      // Ensure token is included for this request
      const currentToken = localStorage.getItem('token');

      if (!currentToken) {
        setError('Authentication required');
        throw new Error('Authentication required');
      }

      const res = await axiosInstance.put(
        `/users/me/addresses/${addressId}`,
        addressData,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      return res.data;
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || 'Failed to update address';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Delete an address
  const deleteAddress = async (addressId) => {
    try {
      // Ensure token is included for this request
      const currentToken = localStorage.getItem('token');

      if (!currentToken) {
        setError('Authentication required');
        throw new Error('Authentication required');
      }

      await axiosInstance.delete(`/users/me/addresses/${addressId}`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      return true;
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail || 'Failed to delete address';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const value = {
    currentUser,
    loading,
    error,
    otpSent,
    phoneNumber,
    token, // Expose token to consumers
    sendPhoneOTP,
    verifyPhoneOTP,
    checkUserExists,
    logout,
    updateProfile,
    getUserAddresses,
    addAddress,
    updateAddress,
    deleteAddress,
    isAuthenticated: !!currentUser && !!token, // Check both user and token
    isAdmin: currentUser?.is_admin || false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};
